<script setup lang="ts">
import type { LoginModalVariant } from '#auth/composables/useLoginModal'
import { useAuth } from '#auth/composables/useAuth'
import { bindField, revealErrors } from '#core/utils/bind-field'
import { Alert, AlertDescription, AlertTitle } from '@forgd/ui-kit/alert'
import { Field, FieldContent, FieldError, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { PasswordInput } from '@forgd/ui-kit/blocks/password-input'
import { Button } from '@forgd/ui-kit/button'
import { Input } from '@forgd/ui-kit/input'
import { UsersIcon } from '@heroicons/vue/24/outline'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { z } from 'zod'

interface UserLogin {
  email: string
  password: string
}

const props = defineProps<{
  email?: string
  variant?: LoginModalVariant
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'advisor'): void
  (e: 'register'): void
  (e: 'forgot'): void
}>()

const auth = useAuth()
const supabase = useSupabaseClient()

const schema = z.object({
  email: z.string().trim().email('Email is required'),
  password: z.string().min(1, 'Password is required'),
})

const form = useForm({
  defaultValues: {
    email: props.email || '',
    password: '',
  } satisfies UserLogin,
  validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
  onSubmitInvalid: ({ formApi }) => revealErrors(formApi),
  validators: { onDynamic: schema },
  onSubmit: ({ value }) => signIn(schema.parse(value)),
})

const errorMessage = ref<string | null>(null)
const loading = ref(false)
const googleLoading = ref(false)

async function signInWithGoogle() {
  errorMessage.value = null
  googleLoading.value = true
  try {
    // On success the browser is redirected to Google, so this page unloads and the
    // loading state does not need to be reset.
    await auth.loginWithGoogle()
  }
  catch {
    errorMessage.value = 'Unable to start Google sign-in. Please try again.'
    googleLoading.value = false
  }
}

async function signIn(loginForm: UserLogin) {
  loading.value = true

  if (!loginForm.email || !loginForm.password) {
    loading.value = false
    return
  }

  const { error } = await supabase.auth.signInWithPassword(loginForm)

  if (error) {
    if (error.status === 0) {
      errorMessage.value = 'Unable to reach server'
    }
    else if (error.code === 'invalid_credentials') {
      errorMessage.value = 'There is no account associated with this email and password. Please try again.'
    }
    else {
      errorMessage.value = error.message
    }
    loading.value = false
    return
  }

  loading.value = false
  emit('success')
}

watch(() => props.email, (email) => {
  if (email) {
    form.setFieldValue('email', email)
  }
})
</script>

<template>
  <div class="flex w-full flex-col gap-5">
    <div>
      <h1 class="text-lg font-semibold text-primal">
        {{ variant === 'continue' ? 'Ready to continue?' : 'Welcome to Forgd' }}
      </h1>
      <p class="text-sm text-secondary">
        {{ variant === 'continue'
          ? 'Login or register to Forgd, and continue with your progress.'
          : 'Login or register to access extra features.' }}
      </p>
    </div>

    <Button
      variant="outline"
      size="lg"
      block
      class="h-12"
      :loading="googleLoading"
      @click="signInWithGoogle"
    >
      <i v-if="!googleLoading" class="i-forgd-google size-5 shrink-0" aria-hidden="true" />
      Continue with Google
    </Button>

    <div class="flex items-center gap-4" aria-hidden="true">
      <div class="h-px flex-1 bg-neutral-600" />
      <span class="text-xs text-chart-label">OR</span>
      <div class="h-px flex-1 bg-neutral-600" />
    </div>

    <form class="space-y-3" novalidate @submit.prevent="form.handleSubmit()">
      <form.Field v-slot="{ field }" name="email">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Login with your email
          </FieldLabel>
          <FieldContent>
            <Input
              v-bind="bindField(field).control"
              :name="field.name"
              type="text"
              size="md"
              placeholder="e.g., youruser@email.com"
            />
            <FieldError v-bind="bindField(field).error" />
          </FieldContent>
        </Field>
      </form.Field>
      <form.Field v-slot="{ field }" name="password">
        <Field>
          <FieldLabel v-bind="bindField(field).label">
            Password
          </FieldLabel>
          <FieldContent>
            <PasswordInput
              v-bind="bindField(field).control"
              :name="field.name"
              size="md"
              autocomplete="current-password"
              placeholder="Enter your password"
            />
            <FieldError v-bind="bindField(field).error" />
          </FieldContent>
        </Field>
      </form.Field>

      <div v-if="errorMessage" class="mt-1 text-sm text-error">
        {{ errorMessage }}
      </div>

      <Button
        size="lg"
        class="h-12"
        block
        type="submit"
        :loading="loading"
        :disabled="googleLoading"
      >
        Login
      </Button>
    </form>

    <!-- Links -->
    <div class="space-y-2 text-sm">
      <slot name="footer">
        <button type="button" class="block font-semibold text-primary-300" @click="emit('forgot')">
          Forgot Password?
        </button>
        <p class="text-gray-600">
          Don’t have an account yet?
          <button type="button" class="font-semibold text-primary-300" @click="emit('register')">
            Join Forgd for Free
          </button>
        </p>
      </slot>
    </div>

    <Alert variant="info">
      <UsersIcon aria-hidden="true" />
      <AlertTitle>Are you a blockchain advisor?</AlertTitle>
      <AlertDescription class="text-xs">
        <p>
          Leverage Forgd software for existing engagements to streamline your portfolio’s operation. Free to use. Contact us to create your account.
        </p>
        <Button variant="outline" size="sm" class="mt-4" @click="emit('advisor')">
          Join as Advisor
        </Button>
      </AlertDescription>
    </Alert>
  </div>
</template>
